import { useEffect, useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { SectionCard } from "@/components/common/SectionCard";
import { usePagination } from "@/components/common/usePagination";
import { compactInputClass, ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  filterLaborPayInvoices,
  LABOR_PAY_INVOICE_SORTS,
  LABOR_PAY_INVOICE_STATUSES,
  sortLaborPayInvoices,
  type LaborPayInvoiceSort,
  type LaborPayInvoiceStatusFilter,
} from "@/lib/manufacturing/laborPayInvoiceBrowse";
import { laborPayStatusLabel } from "@/lib/manufacturing/laborPayList";
import { LABOR_PAY_DEFAULT_PAGE_SIZE, LABOR_PAY_PAGE_SIZES } from "@/lib/manufacturing/laborPayPaging";
import { laborPayRowInWeek, summarizeLaborPayWeek } from "@/lib/manufacturing/laborPayWeekBill";
import { DEFAULT_LABOR_PAY_CUTOFF } from "@/lib/manufacturing/laborPayWeek";
import { ManufacturingLaborPayCutoffBar } from "./ManufacturingLaborPayCutoffBar";
import { ManufacturingLaborPayInvoiceLines } from "./ManufacturingLaborPayInvoiceLines";
import { ManufacturingLaborPayWeekSummary } from "./ManufacturingLaborPayWeekSummary";
import { useManufacturingLaborPayList } from "./useManufacturingLaborPayList";

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function ManufacturingLaborPayList({
  plantId,
  navTick,
  search = "",
  query = "",
  onQueryChange,
  readOnly = false,
}: {
  plantId: string;
  navTick: number;
  search?: string;
  query?: string;
  onQueryChange?: (value: string) => void;
  readOnly?: boolean;
}) {
  const p = useManufacturingLaborPayList(plantId, navTick);
  const [openId, setOpenId] = useState<string | null>(null);
  const [sort, setSort] = useState<LaborPayInvoiceSort>("serial");
  const [status, setStatus] = useState<LaborPayInvoiceStatusFilter>("all");
  const [showingAll, setShowingAll] = useState(false);
  const cutoff = p.cutoffConfig ?? DEFAULT_LABOR_PAY_CUTOFF;
  const bill = useMemo(() => summarizeLaborPayWeek(p.rows, cutoff), [p.rows, cutoff]);
  const scoped = useMemo(
    () =>
      showingAll
        ? p.rows
        : p.rows.filter((row) => laborPayRowInWeek(row.admitted_at, bill.start, bill.end)),
    [showingAll, p.rows, bill.start, bill.end],
  );
  const rows = useMemo(
    () => sortLaborPayInvoices(filterLaborPayInvoices(scoped, search, status), sort),
    [scoped, search, status, sort]
  );
  const page = usePagination(rows, LABOR_PAY_DEFAULT_PAGE_SIZE);

  useEffect(() => {
    page.setPageIndex(0);
  }, [search, status, sort, showingAll, page.setPageIndex]);

  useEffect(() => {
    if (!openId) return;
    const index = rows.findIndex((row) => row.unit_id === openId);
    if (index < 0) return;
    const next = Math.floor(index / page.pageSize);
    if (next !== page.pageIndex) page.setPageIndex(next);
  }, [openId, rows, page.pageSize, page.pageIndex, page.setPageIndex]);

  return (
    <SectionCard
      className="h-full min-w-0"
      title={showingAll ? "All labor invoices" : "This week's labor"}
      description={
        showingAll
          ? "Full invoice history. This week is the buildings admitted since the last cutoff."
          : "Buildings admitted since the last cutoff. Open a row to check its lines."
      }
    >
      {p.error ? (
        <p className="mb-2 text-sm text-red-700 dark:text-red-300">{p.error}</p>
      ) : null}
      {p.missingTable ? (
        <p className="mb-2 text-xs text-amber-800 dark:text-amber-200">
          Review stamps need docs/sql/mfg_labor_pay_list_v1.sql (and mfg_labor_pay_batches_v1.sql for week close).
        </p>
      ) : null}
      <ManufacturingLaborPayWeekSummary
        bill={bill}
        showingAll={showingAll}
        onShowWeek={() => setShowingAll(false)}
        onShowAll={() => setShowingAll(true)}
      />
      <ManufacturingLaborPayCutoffBar
        cutoffLabel={p.cutoffLabel}
        config={p.cutoffConfig}
        canEdit={!readOnly && p.canCloseWeek && !p.missingTable}
        busy={p.actionBusy}
        onChange={(next) => void p.saveCutoff(next)}
        onCloseWeek={() => void p.closeWeek()}
      />
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-zinc-400">
          Sort
          <select
            className={`${compactInputClass} w-40`}
            value={sort}
            aria-label="Sort invoices"
            onChange={(e) => setSort(e.target.value as LaborPayInvoiceSort)}
          >
            {LABOR_PAY_INVOICE_SORTS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-zinc-400">
          Status
          <select
            className={`${compactInputClass} w-48`}
            value={status}
            aria-label="Filter invoice status"
            onChange={(e) => setStatus(e.target.value as LaborPayInvoiceStatusFilter)}
          >
            {LABOR_PAY_INVOICE_STATUSES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <input
          className={`${compactInputClass} min-w-[12rem] flex-1`}
          value={query}
          onChange={(e) => onQueryChange?.(e.target.value)}
          placeholder="Search serial, date, option, label…"
          aria-label="Search serial, date, option, label"
        />
      </div>
      {p.busy && rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          {showingAll
            ? "No serials on the pay list. Build complete is the pay-trigger until you change it. Drag a finished unit into that column."
            : "No buildings were admitted this week. All invoices shows anything still open from earlier."}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
          {page.paginatedItems.map((row) => {
            const laborUnmatched = row.itemized && row.unmatched_labor_count > 0;
            return (
            <li
              key={row.unit_id}
              className={
                laborUnmatched
                  ? "my-1 rounded-lg border border-red-400 bg-red-50 px-2 py-2 dark:border-red-700 dark:bg-red-950/40"
                  : "py-2"
              }
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-sm text-gray-900 dark:text-zinc-100">{row.serial}</div>
                <div className="text-xs text-gray-500 dark:text-zinc-400">
                  {row.recipe_label} · {row.phase_name}
                  {row.admitted_at ? ` · ${new Date(row.admitted_at).toLocaleDateString()}` : ""}
                  {row.itemized && row.rate_item_count === 0 ? " · no company rate matched" : ""}
                  {row.itemized && row.unmatched_labor_count > 0
                    ? ` · ${row.unmatched_labor_count} labor unmatched`
                    : ""}
                  {row.itemized && row.unmatched_model_count > 0
                    ? ` · ${row.unmatched_model_count} not in models`
                    : ""}
                  {row.in_pay_trigger_column ? "" : " · left column"}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className={ghostBtnClass}
                  onClick={() => setOpenId(openId === row.unit_id ? null : row.unit_id)}
                >
                  {openId === row.unit_id ? "Hide" : "Lines"}
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                  {money(row.payout_usd)}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-700 dark:bg-zinc-800 dark:text-zinc-200">
                  {laborPayStatusLabel(row.status)}
                </span>
                {row.status !== "approved" &&
                row.status !== "void" &&
                row.status !== "payment_requested" &&
                row.status !== "batched" &&
                !p.missingTable ? (
                  <>
                    {!readOnly && p.canOps && !row.ops_reviewed_at ? (
                      <button
                        type="button"
                        className={ghostBtnClass}
                        disabled={p.actionBusy}
                        onClick={() => void p.stamp(row, "ops")}
                      >
                        Ops OK
                      </button>
                    ) : null}
                    {p.canPlant && !row.plant_reviewed_at ? (
                      <button
                        type="button"
                        className={ghostBtnClass}
                        disabled={p.actionBusy}
                        onClick={() => void p.stamp(row, "plant")}
                      >
                        Labor OK
                      </button>
                    ) : null}
                  </>
                ) : null}
                {!readOnly && p.canRequest(row) && row.entry_id && !p.missingTable ? (
                  <button
                    type="button"
                    className={ghostBtnClass}
                    disabled={p.actionBusy}
                    onClick={() => void p.requestPay(row)}
                  >
                    Request payment
                  </button>
                ) : null}
              </div>
              </div>
              {openId === row.unit_id ? (
                <ManufacturingLaborPayInvoiceLines
                  unitId={row.unit_id}
                  readOnly={readOnly}
                  onUpdated={() => void p.reload()}
                />
              ) : null}
            </li>
            );
          })}
        </ul>
      )}
      <PaginationBar
        pageIndex={page.pageIndex}
        pageCount={page.pageCount}
        pageSize={page.pageSize}
        totalCount={page.totalCount}
        firstShown={page.firstShown}
        lastShown={page.lastShown}
        onPageChange={page.setPageIndex}
        onPageSizeChange={page.setPageSize}
        pageSizeOptions={LABOR_PAY_PAGE_SIZES}
        noun="invoices"
        alwaysShow
      />
    </SectionCard>
  );
}
